import React, { useEffect, useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import type { RecentWorkUrls, UserProfilePreviewLoadState } from '../types';
import { confluenceLinksToAbsoluteUrl } from '../utils/confluence-links-to-absolute-url';
import type { UserProfilePreviewRelayContent_reportingLines$data } from './__generated__/UserProfilePreviewRelayContent_reportingLines.graphql';
import type { UserProfilePreviewRelayContent_reportingLines$key } from './__generated__/UserProfilePreviewRelayContent_reportingLines.graphql';
import type { UserProfilePreviewRelayContent_smarts$data } from './__generated__/UserProfilePreviewRelayContent_smarts.graphql';
import type { UserProfilePreviewRelayContent_smarts$key } from './__generated__/UserProfilePreviewRelayContent_smarts.graphql';
import type { UserProfilePreviewRelayContent_user$key } from './__generated__/UserProfilePreviewRelayContent_user.graphql';
import type { UserProfilePreviewRelayContent_workedOn$data } from './__generated__/UserProfilePreviewRelayContent_workedOn.graphql';
import type { UserProfilePreviewRelayContent_workedOn$key } from './__generated__/UserProfilePreviewRelayContent_workedOn.graphql';
import { UserProfilePreview } from './UserProfilePreview';
import type { UserProfilePreviewProps } from './UserProfilePreview';

type UserProfilePreviewRelayContentProps = Omit<
	React.ComponentProps<typeof UserProfilePreview>,
	'recentWorkData'
> & {
	collaboratorsFailed?: boolean;
	onLoad?: (state: UserProfilePreviewLoadState) => void;
	recentWorkFailed?: boolean;
	reportingLinesFailed?: boolean;
	reportingLinesFragment?: UserProfilePreviewRelayContent_reportingLines$key;
	smartsFragment?: UserProfilePreviewRelayContent_smarts$key;
	userFragment?: UserProfilePreviewRelayContent_user$key;
	workedOnFragment?: UserProfilePreviewRelayContent_workedOn$key;
};

type ActivityObjectData = NonNullable<
	NonNullable<UserProfilePreviewRelayContent_workedOn$data['edges'][number]>['node']
>['object']['data'];

/**
 * Derives a canonical URL for a recent-activity object, covering every
 * URL-bearing activity type returned by the full profile-page query. Returns
 * an empty string when the object type has no resolvable URL.
 */
function extractActivityUrl(objectData: ActivityObjectData | null | undefined): string {
	if (!objectData) {
		return '';
	}

	switch (objectData.__typename) {
		case 'ConfluencePage':
		case 'ConfluenceBlogPost':
		case 'ConfluenceWhiteboard':
		case 'ConfluenceDatabase':
		case 'ConfluenceEmbed':
		case 'ConfluenceFooterComment':
		case 'ConfluenceInlineComment':
			return objectData.links?.base && objectData.links?.webUi
				? confluenceLinksToAbsoluteUrl(objectData.links.base, objectData.links.webUi)
				: '';
		case 'JiraIssue':
		case 'JiraPlatformComment':
		case 'JiraServiceManagementComment':
		case 'DevOpsDocument':
		case 'DevOpsDesign':
			return objectData.webUrl ?? '';
		case 'TrelloBoard':
		case 'TrelloCard':
			return objectData.trelloUrl ?? '';
		case 'TownsquareGoal':
		case 'TownsquareProject':
		case 'TownsquareComment':
		case 'ExternalDocument':
		case 'DevOpsPullRequestDetails':
		case 'ExternalPullRequest':
		case 'ExternalRemoteLink':
		case 'MercuryFocusArea':
		case 'MercuryPortfolio':
		case 'RadarPosition':
		case 'RadarView':
		case 'CompassComponent':
			return objectData.url ?? '';
		case 'BitbucketPullRequest':
			return objectData.href ?? '';
		case 'LoomVideo':
			return objectData.loomUrl ?? '';
		case 'AssetsObject':
		case 'AssetsObjectType':
		case 'AssetsSchema':
			return objectData.links?.web ?? '';
		default:
			return '';
	}
}

/**
 * Computes the de-duplication key for a recent-activity object, mirroring the
 * full profile page's `deduplicateId` behaviour:
 * - Confluence comments de-duplicate on their container (page/blog/whiteboard),
 *   so multiple comments on the same page collapse to a single entry
 *   (equivalent to `deduplicateId: ['container.id']`).
 * - Jira comments de-duplicate on their parent issue
 *   (equivalent to `deduplicateId: ['issue.id']`).
 * - Everything else de-duplicates on its own URL
 *   (equivalent to `deduplicateId: ['objectId']`).
 *
 * Falls back to the object's own URL when the container/issue is unavailable.
 */
function getActivityDedupeKey(objectData: ActivityObjectData | null | undefined): string {
	const ownUrl = extractActivityUrl(objectData);
	if (!objectData) {
		return ownUrl;
	}
	switch (objectData.__typename) {
		case 'ConfluenceFooterComment':
		case 'ConfluenceInlineComment': {
			const container = objectData.container;
			const containerUrl =
				container && 'links' in container && container.links?.base && container.links?.webUi
					? confluenceLinksToAbsoluteUrl(container.links.base, container.links.webUi)
					: '';
			return containerUrl || ownUrl;
		}
		case 'JiraPlatformComment':
		case 'JiraServiceManagementComment':
			return objectData.issue?.webUrl || ownUrl;
		default:
			return ownUrl;
	}
}

/** Maps recent activity to deduplicated display URLs. */
export function mapRelayWorkedOnConnectionToRecentWorkItems(
	data: UserProfilePreviewRelayContent_workedOn$data,
): RecentWorkUrls {
	// De-duplicate by the profile page's `deduplicateId` key (comments collapse
	// onto their container/issue), keeping the first occurrence, then resolve
	// each survivor to its display URL.
	const seenKeys = new Set<string>();
	const result: string[] = [];

	for (const edge of data.edges) {
		const objectData = edge?.node?.object?.data;
		const url = extractActivityUrl(objectData);
		if (!url) {
			continue;
		}
		const dedupeKey = getActivityDedupeKey(objectData);
		if (seenKeys.has(dedupeKey)) {
			continue;
		}
		seenKeys.add(dedupeKey);
		result.push(url);
	}

	return result;
}

type Collaborator = NonNullable<UserProfilePreviewProps['collaborators']>[number];

/** Maps recommended users to collaborators, excluding the profile owner. */
export function mapRelaySmartsToCollaborators(
	data: UserProfilePreviewRelayContent_smarts$data,
	accountId?: string,
): Collaborator[] {
	const recommended = data.recommendedUser ?? [];
	if (!recommended.length) {
		return [];
	}

	return recommended.reduce<Collaborator[]>((acc, node) => {
		const user = node?.user;
		if (!user) {
			return acc;
		}
		const id = user.accountId;
		if (!id || (accountId && id === accountId)) {
			return acc;
		}
		acc.push({
			accountId: id ?? null,
			name: user.name ?? null,
			avatarUrl: user.picture ?? null,
			email: user.email ?? null,
			jobTitle: user.extendedProfile?.jobTitle ?? null,
		});
		return acc;
	}, []);
}

type HierarchyData = NonNullable<UserProfilePreviewProps['hierarchyData']>;

type ReportingLines = Extract<
	UserProfilePreviewRelayContent_reportingLines$data['reportingLines'],
	{ __typename: 'KnowledgeDiscoveryReportingLines' }
>;

/** `user` / `managers` / `reports` rows — same shape as `UserProfilePreviewRelayContent_reportingLines` fragment data. */
function mapKdReportingLinesUser(user: ReportingLines['user']): HierarchyData['user'] | null {
	if (!user?.accountIdentifier) {
		return null;
	}
	return {
		accountId: user.accountIdentifier,
		name: user.pii?.name ?? '',
		avatarUrl: user.pii?.picture ?? null,
		jobTitle: user.pii?.extendedProfile?.jobTitle ?? null,
		numberOfDirectReports: user.numberOfDirectReports ?? undefined,
		numberOfTotalReports: user.numberOfTotalReports ?? undefined,
	};
}

/** Maps reporting lines to a hierarchy including managers, reports, and peers. */
export function mapKnowledgeDiscoveryReportingLinesToHierarchyData(
	data: UserProfilePreviewRelayContent_reportingLines$data | null | undefined,
): HierarchyData | undefined {
	if (!data?.reportingLines) {
		return undefined;
	}
	const rl = data.reportingLines;
	if (rl.__typename !== 'KnowledgeDiscoveryReportingLines') {
		return undefined;
	}
	const user = mapKdReportingLinesUser(rl.user);
	if (!user) {
		return undefined;
	}
	const managers = (rl.managers ?? [])
		.map((manager) => mapKdReportingLinesUser(manager))
		.filter((manager): manager is HierarchyData['user'] => manager != null);
	const reports =
		rl.reports
			?.map((report) => mapKdReportingLinesUser(report))
			.filter((report): report is HierarchyData['user'] => report != null) ?? [];
	const peers =
		rl.peers
			?.map((peer) => mapKdReportingLinesUser(peer))
			.filter((peer): peer is HierarchyData['user'] => peer != null) ?? [];
	return {
		managers: managers.length ? managers : undefined,
		user,
		reports: reports.length ? reports : undefined,
		peers: peers.length ? peers : undefined,
	};
}

/** Maps Relay profile datasets into the profile preview presentation. */
export function UserProfilePreviewRelayContent({
	onLoad,
	userFragment,
	workedOnFragment,
	smartsFragment,
	reportingLinesFragment,
	collaboratorsFailed = false,
	recentWorkFailed = false,
	reportingLinesFailed = false,
	...props
}: UserProfilePreviewRelayContentProps): React.JSX.Element {
	const userProfileData = useFragment(
		graphql`
			fragment UserProfilePreviewRelayContent_user on KnowledgeDiscoveryQueryApi
			@argumentDefinitions(
				accountId: { type: "String!" }
				requestingUserId: { type: "String!" }
				tenantId: { type: "String!" }
			) {
				singleUser(accountId: $accountId, requestingUserId: $requestingUserId, tenantId: $tenantId)
					@optIn(to: "KnowledgeDiscovery Single User") {
					__typename
					... on KnowledgeDiscoverySingleUser {
						user {
							id
							name
							picture
							email
							jobTitle
							department
							location
							organization
							thirdPartyIntegrations {
								externalId
								integrationId
								product
							}
						}
					}
				}
			}
		`,
		userFragment ?? null,
	);

	const workedOnData = useFragment(
		graphql`
			fragment UserProfilePreviewRelayContent_workedOn on ActivityConnection {
				edges {
					node {
						object {
							data {
								__typename
								... on ConfluencePage {
									links {
										base
										webUi
									}
								}
								... on ConfluenceBlogPost {
									links {
										base
										webUi
									}
								}
								... on ConfluenceWhiteboard {
									links {
										base
										webUi
									}
								}
								... on ConfluenceDatabase {
									links {
										base
										webUi
									}
								}
								... on ConfluenceEmbed {
									links {
										base
										webUi
									}
								}
								... on ConfluenceFooterComment {
									links {
										base
										webUi
									}
									container {
										__typename
										... on ConfluencePage {
											links {
												base
												webUi
											}
										}
										... on ConfluenceBlogPost {
											links {
												base
												webUi
											}
										}
										... on ConfluenceWhiteboard {
											links {
												base
												webUi
											}
										}
									}
								}
								... on ConfluenceInlineComment {
									links {
										base
										webUi
									}
									container {
										__typename
										... on ConfluencePage {
											links {
												base
												webUi
											}
										}
										... on ConfluenceBlogPost {
											links {
												base
												webUi
											}
										}
										... on ConfluenceWhiteboard {
											links {
												base
												webUi
											}
										}
									}
								}
								... on TownsquareGoal {
									url
								}
								... on TownsquareProject {
									url
								}
								... on TownsquareComment {
									url
								}
								... on JiraIssue {
									webUrl
								}
								... on JiraPlatformComment {
									webUrl
									issue {
										webUrl
									}
								}
								... on JiraServiceManagementComment {
									webUrl
									issue {
										webUrl
									}
								}
								... on TrelloBoard {
									trelloUrl: url
								}
								... on TrelloCard {
									trelloUrl: url
								}
								... on DevOpsDocument {
									webUrl: url
								}
								... on ExternalDocument {
									url
								}
								... on DevOpsPullRequestDetails {
									url
								}
								... on ExternalPullRequest {
									url
								}
								... on DevOpsDesign {
									webUrl: url
								}
								... on ExternalRemoteLink {
									url
								}
								... on MercuryFocusArea {
									url
								}
								... on MercuryPortfolio {
									url
								}
								... on RadarPosition {
									url
								}
								... on RadarView {
									url
								}
								... on CompassComponent {
									url
								}
								... on BitbucketPullRequest {
									href
								}
								... on LoomVideo {
									loomUrl: url
								}
								... on AssetsObject {
									links {
										web
									}
								}
								... on AssetsObjectType {
									links {
										web
									}
								}
								... on AssetsSchema {
									links {
										web
									}
								}
							}
						}
					}
				}
			}
		`,
		workedOnFragment ?? null,
	);

	const smartsData = useFragment(
		graphql`
			fragment UserProfilePreviewRelayContent_smarts on SmartsQueryApi
			@argumentDefinitions(recommendationsQuery: { type: "SmartsRecommendationsQuery!" }) {
				recommendedUser(recommendationsQuery: $recommendationsQuery) {
					user {
						accountId
						name
						picture
						... on AtlassianAccountUser {
							email
							extendedProfile {
								jobTitle
							}
						}
					}
				}
			}
		`,
		smartsFragment ?? null,
	);

	const reportingLinesData = useFragment(
		graphql`
			fragment UserProfilePreviewRelayContent_reportingLines on KnowledgeDiscoveryQueryApi
			@argumentDefinitions(
				aaid: { type: "String!" }
				orgId: { type: "String!" }
				siteId: { type: "String!" }
			) {
				reportingLines(aaid: $aaid, orgId: $orgId, siteId: $siteId)
					@optIn(to: "KnowledgeDiscovery Get reporting lines") {
					__typename
					... on KnowledgeDiscoveryReportingLines {
						user {
							accountIdentifier
							numberOfDirectReports
							numberOfTotalReports
							pii {
								name
								picture
								extendedProfile {
									jobTitle
								}
							}
						}
						managers {
							accountIdentifier
							numberOfDirectReports
							numberOfTotalReports
							pii {
								name
								picture
								extendedProfile {
									jobTitle
								}
							}
						}
						reports {
							accountIdentifier
							numberOfDirectReports
							numberOfTotalReports
							pii {
								name
								picture
								extendedProfile {
									jobTitle
								}
							}
						}
						peers {
							accountIdentifier
							numberOfDirectReports
							numberOfTotalReports
							pii {
								name
								picture
								extendedProfile {
									jobTitle
								}
							}
						}
					}
				}
			}
		`,
		reportingLinesFragment ?? null,
	);
	const relayHierarchyData = useMemo(
		() => mapKnowledgeDiscoveryReportingLinesToHierarchyData(reportingLinesData),
		[reportingLinesData],
	);
	const recentWorkData = useMemo(() => {
		if (!workedOnData) {
			return undefined;
		}
		const mapped = mapRelayWorkedOnConnectionToRecentWorkItems(workedOnData);
		return mapped.length ? mapped : undefined;
	}, [workedOnData]);

	const relayProfileData = useMemo(() => {
		const result = userProfileData?.singleUser;
		if (!result || result.__typename !== 'KnowledgeDiscoverySingleUser') {
			return undefined;
		}
		const user = result.user;
		if (!user?.id) {
			return undefined;
		}
		return {
			accountId: user.id,
			name: user.name ?? '',
			avatarUrl: user.picture ?? null,
			email: user.email ?? null,
			jobTitle: user.jobTitle ?? null,
			department: user.department ?? null,
			location: user.location ?? null,
			organization: user.organization ?? null,
			thirdPartyIntegrations:
				user.thirdPartyIntegrations?.map((integration) => ({
					externalId: integration.externalId,
					integrationId: integration.integrationId,
					product: integration.product,
				})) ?? null,
		};
	}, [userProfileData]);

	const relayCollaborators = useMemo(() => {
		if (!smartsData) {
			return undefined;
		}
		const mapped = mapRelaySmartsToCollaborators(smartsData, relayProfileData?.accountId);
		return mapped.length ? mapped : undefined;
	}, [smartsData, relayProfileData?.accountId]);

	const resolvedProfileData = relayProfileData ?? props.profileData;
	const hasResolvedProfile = Boolean(resolvedProfileData?.name || resolvedProfileData?.accountId);

	useEffect(() => {
		onLoad?.({ collaboratorsFailed, recentWorkFailed, reportingLinesFailed });
	}, [onLoad, collaboratorsFailed, recentWorkFailed, reportingLinesFailed]);

	if (!hasResolvedProfile) {
		throw new Error('UserProfilePreviewRelay: no user profile data resolved');
	}

	return (
		<UserProfilePreview
			{...props}
			profileData={resolvedProfileData}
			hierarchyData={relayHierarchyData ?? props.hierarchyData}
			recentWorkData={recentWorkData}
			collaborators={relayCollaborators ?? props.collaborators}
		/>
	);
}
